import { apiClient } from './apiClient.js';
import type { ApiResponse } from '@onboarding-dashboard/shared';

export interface CanopusPhaseTiming {
  name: string;
  durationSec: number | null;
  avgSec: number | null;
  inProgress: boolean;
}

export interface CanopusStep {
  name: string;
  phase: string;
  phaseIndex: number;
  source?: number | null;
  done: boolean;
  clickTimeIso: string | null;
}

export interface CanopusActiveCase {
  procedureId: string;
  surgeonId: string;
  surgeonName: string;
  procedureName: string;
  profileName: string | null;
  specialty: string | null;
  startTimeIso: string;
  elapsedSec: number;
  currentPhaseIndex: number;
  currentPhaseName: string | null;
  currentStepName: string | null;
  currentStepIndex: number;
  stepsCompleted: number;
  totalSteps: number;
  hasAvg: boolean;
  caseCount: number;
  avgDurationSec: number | null;
  estRemainingSec: number | null;
  pace: 'on' | 'slight' | 'over' | 'new' | null;
  runningLong: boolean;
  /**
   * Contextual chips surfaced on the live OR card. Currently only the
   * raw-baseline elapsed-vs-avg alert is emitted; TFI-adjusted variants
   * are blocked on the TFI service. Empty array when nothing to flag.
   */
  alerts: string[];
  phases: CanopusPhaseTiming[];
  steps: CanopusStep[];
  participantIds: string[];
  /** Display name of the Scrub Tech on this case, when known. Backend leaves null until staff-by-position is wired. Demo overlay fills it. */
  scrubTechName: string | null;
  /** Team Familiarity Index for this case (0..100). Null until real TFI data is available; demo overlay fills it. */
  tfi: number | null;
  /** Display name of the recording device for this OR (e.g. "iPad", "Apple Vision Pro"). Null until real device wiring is available; demo overlay fills it. */
  deviceName: string | null;
  // True when the client has stopped reporting (lastActivityTimeUnix
  // missing or older than 30 min). Frontend renders this as a yellow
  // "No active tracking" warning chip on the OR card.
  trackingStale: boolean;
  // ISO timestamp of the most recent client interaction. Null when the
  // backing `lastActivityTimeUnix` field is missing on the doc.
  lastActivityIso: string | null;
}

export interface CanopusRoomStatus {
  id: string;
  name: string;
  status: 'active' | 'long' | 'done' | 'idle' | 'new';
  activeCase: CanopusActiveCase | null;
  lastCase: {
    procedureName: string;
    surgeonName: string;
    endTimeIso: string;
  } | null;
}

export interface CanopusCaseLogEntry {
  procedureId: string;
  surgeonName: string;
  procedureName: string;
  roomName: string;
  startTimeIso: string;
  endTimeIso: string | null;
  totalTimeSec: number;
  stepsCompleted: number;
  totalSteps: number;
  /** Historical average duration in seconds for this surgeon + procedure combination. Null when no baseline exists yet. */
  avgDurationSec: number | null;
  /** Number of historical cases backing the avg. 0 when no baseline. */
  caseCount: number;
}

export interface CanopusEvent {
  timestampIso: string;
  roomName: string;
  procedureName: string;
  stepName: string;
  phase: string;
}

export interface CanopusOrStatus {
  facilityName: string;
  generatedAtIso: string;
  rooms: CanopusRoomStatus[];
  todaysCases: CanopusCaseLogEntry[];
  events: CanopusEvent[];
}

export type CanopusSIPeriod = 'today' | '7d' | '30d' | '90d' | 'ytd' | 'all';

export interface CanopusSIModuleCounts {
  pickList: number;
  trayLayout: number;
  roomLayout: number;
  refGuide: number;
  procNotes: number;
}

export interface CanopusSIAggregates {
  period: CanopusSIPeriod;
  generatedAtIso: string;
  overview: {
    total: number;
    avgPerCase: number;
    cases: number;
    liveCaseRatio: number;
    offCase: number;
  };
  byModule: CanopusSIModuleCounts;
  byOR: Array<{
    roomId: string;
    roomName: string;
    total: number;
    modules: CanopusSIModuleCounts;
    surgeonName: string | null;
    procedureName: string | null;
    liveCase: boolean;
    /** Number of cases recorded in the period for this OR. 0 when not computed. */
    cases: number;
    /** Average SIs per recorded case in this OR. 0 when no cases. */
    avgPerCase: number;
    /** Most-fired SI module label for this OR. Empty string when no SIs. */
    topModule: string;
  }>;
  byStaff: Array<{
    userId: string;
    name: string | null;
    position: string | null;
    /** True when this entry is a shared OR device row (merged per platform). */
    isDevice: boolean;
    /** CreatorX DevicePlatform enum for device rows (e.g. "IPhonePlayer"). */
    devicePlatform: string | null;
    /** Number of physical devices merged into this row. 1 for a person. */
    deviceCount: number;
    total: number;
    offCase: number;
    topModule: string;
    /** Number of cases the staff member participated in over the period. 0 when not computed. */
    cases: number;
    /** Average SIs per recorded case for this staff member. 0 when no cases. */
    avgPerCase: number;
  }>;
  byProfile: Array<{
    prefCardId: string;
    name: string;
    surgeonName: string | null;
    procedureName: string | null;
    total: number;
    cases: number;
    avgPerCase: number;
    modules: CanopusSIModuleCounts;
  }>;
  bySurgeon: Array<{
    surgeonId: string;
    name: string;
    specialty: string | null;
    total: number;
    cases: number;
    avgPerCase: number;
    topModule: string;
  }>;
  bySpecialty: Array<{
    name: string;
    total: number;
    cases: number;
    avgPerCase: number;
    topModule: string;
  }>;
  byProcedure: Array<{
    name: string;
    specialty: string | null;
    total: number;
    cases: number;
    avgPerCase: number;
    topModule: string;
  }>;
  heatmap: Array<{ day: string; total: number; modules: CanopusSIModuleCounts }>;
  monthlyTrend: Array<{
    month: string;
    total: number;
    offCase: number;
    cases: number;
    avgPerCase: number;
  }>;
  timing: {
    preOperative: { count: number; pct: number };
    intraOperative: { count: number; pct: number };
    postOperative: { count: number; pct: number };
    outsideSurgery: { count: number; pct: number };
  };
}

export interface CanopusStaffMember {
  userId: string;
  name: string | null;
  email: string | null;
  /** Clinical position (e.g. "Scrub Tech", "Circ Nurse"). Sourced from the member doc's `position` field. Displayed in the UI as "Role". */
  position: string | null;
  onDuty: boolean;
  currentRoom: string | null;
  wasActiveEarlierToday: boolean;
  lastRoomToday: string | null;
  /** Procedure name of the most recent PR this user appeared in. Null when no PR in the data window references them. */
  lastProcedureName: string | null;
  siToday: number;
  siOffCase: number;
}

export interface CanopusStaffStatus {
  generatedAtIso: string;
  members: CanopusStaffMember[];
}

export interface CanopusHistoricalCase {
  procedureId: string;
  dateIso: string;
  surgeonId: string;
  surgeonName: string;
  specialty: string | null;
  procedureName: string;
  roomName: string;
  totalTimeSec: number;
  avgDurationSec: number | null;
  caseCount: number;
  profileName: string | null;
  startTimeIso: string;
  intraoperativeStartTimeIso: string | null;
  intraoperativeEndTimeIso: string | null;
  phases: CanopusPhaseTiming[];
  steps: CanopusStep[];
  /** Display name of the Scrub Tech on this case. Null when not known. */
  scrubTechName: string | null;
  /** Display name of the Circulating Nurse on this case. Null when not known. */
  circNurseName: string | null;
  /** Recording device label (e.g. "iPad", "Apple Vision Pro"). Null when not known. */
  deviceName: string | null;
}

export interface CanopusHistoricalCasesResult {
  generatedAtIso: string;
  period: CanopusSIPeriod;
  cases: CanopusHistoricalCase[];
}

export interface CanopusLeaderboardEntry {
  name: string;
  specialty: string | null;
  position: string | null;
  cases: number;
  avgDur: number | null;
  avgVariance: number | null;
  consistency: number | null;
  improvement: number | null;
  utilization: number | null;
  siTotal: number;
  siOffCase: number | null;
  siPerCase: number | null;
  bestDay: string | null;
  trend: (number | null)[];
}

export interface CanopusPerformanceLeaderboard {
  period: CanopusSIPeriod;
  generatedAtIso: string;
  surgeons: CanopusLeaderboardEntry[];
  staff: CanopusLeaderboardEntry[];
  rooms: CanopusLeaderboardEntry[];
  specialties: CanopusLeaderboardEntry[];
  procedures: CanopusLeaderboardEntry[];
  /** Short month labels (e.g. 'Dec', 'Jan') matching the order of each entry's `trend` array. */
  trendMonths: string[];
}

export interface CanopusTrendsMonthlyEntry {
  monthIso: string;
  monthLabel: string;
  cases: number;
  avgDurMin: number | null;
  avgVarianceMin: number | null;
  /** % of eligible cases that ran >15% over the historical baseline ("Cases Above Variance"). */
  longRatePct: number | null;
  /** % of eligible cases that ran >15% under the historical baseline ("Cases Below Variance"). */
  shortRatePct: number | null;
  /** Avg minutes a long case ran over the baseline. Null when no long cases. */
  runtimeAboveMin: number | null;
  /** Avg minutes a short case ran under the baseline (positive number). Null when no short cases. */
  runtimeBelowMin: number | null;
  siTotal: number;
  siOffCase: number;
  siPerCase: number | null;
}

export interface CanopusTrendsSpecialtyVariance {
  specialty: string;
  baselineVarMin: number | null;
  currentVarMin: number | null;
  reductionPct: number | null;
  cases: number;
}

export interface CanopusTrendsSurgeonProcedureVariance {
  surgeonName: string;
  procedureName: string;
  baselineVarMin: number | null;
  currentVarMin: number | null;
  reductionPct: number | null;
  baselineCases: number;
  totalCases: number;
  trend: 'improving' | 'stable' | 'building';
}

export interface CanopusTrendsVarianceHeatmap {
  rooms: string[];
  days: string[];
  matrix: (number | null)[][];
}

export interface CanopusTrendsAdoptionBucket {
  bucket: string;
  avgVarianceMin: number | null;
  cases: number;
}

export interface CanopusTrendsSurgeonDurationIntel {
  surgeonName: string;
  topProcedure: string | null;
  cases: number;
  avgDurMin: number | null;
  longestPhaseName: string | null;
  longestPhaseAvgMin: number | null;
  runningLongPct: number | null;
  avgPreOpSetupMin: number | null;
}

export interface CanopusTrendsRoomGap {
  roomName: string;
  lastGapMin: number | null;
  avgGapMin: number | null;
  gapsMeasured: number;
}

export interface CanopusTrendsKpis {
  totalRecordedCases: number;
  avgDurationMin: number | null;
  profilesCount: number;
  surgeonsCount: number;
}

export interface CanopusTrendsResult {
  period: CanopusSIPeriod;
  generatedAtIso: string;
  monthlyTrends: CanopusTrendsMonthlyEntry[];
  specialtyVariance: CanopusTrendsSpecialtyVariance[];
  surgeonProcedureVariance: CanopusTrendsSurgeonProcedureVariance[];
  varianceHeatmap: CanopusTrendsVarianceHeatmap;
  adoptionCurve: CanopusTrendsAdoptionBucket[];
  reliableAvgsPct: number;
  surgeonDurationIntel: CanopusTrendsSurgeonDurationIntel[];
  roomGapTimes: CanopusTrendsRoomGap[];
  kpis: CanopusTrendsKpis;
}

export const canopusCommandService = {
  async getOrStatus(facilityConfigId: string): Promise<CanopusOrStatus | null> {
    const res: ApiResponse<CanopusOrStatus> = await apiClient.get<CanopusOrStatus>(
      `/canopus-command/facilities/${facilityConfigId}/or-status`
    );
    if (!res.success || !res.data) return null;
    return res.data;
  },

  async getStaffStatus(facilityConfigId: string): Promise<CanopusStaffStatus | null> {
    const res: ApiResponse<CanopusStaffStatus> = await apiClient.get<CanopusStaffStatus>(
      `/canopus-command/facilities/${facilityConfigId}/staff`
    );
    if (!res.success || !res.data) return null;
    return res.data;
  },

  async getSurgicalInquiriesAggregates(
    facilityConfigId: string,
    period: CanopusSIPeriod
  ): Promise<CanopusSIAggregates | null> {
    const res: ApiResponse<CanopusSIAggregates> = await apiClient.get<CanopusSIAggregates>(
      `/canopus-command/facilities/${facilityConfigId}/surgical-inquiries?period=${period}`
    );
    if (!res.success || !res.data) return null;
    return res.data;
  },

  async getHistoricalCases(
    facilityConfigId: string,
    period: CanopusSIPeriod
  ): Promise<CanopusHistoricalCasesResult | null> {
    const res = await apiClient.get<CanopusHistoricalCasesResult>(
      `/canopus-command/facilities/${facilityConfigId}/historical-cases?period=${period}`
    );
    if (!res.success || !res.data) return null;
    return res.data;
  },

  async getPerformanceLeaderboard(
    facilityConfigId: string,
    period: CanopusSIPeriod
  ): Promise<CanopusPerformanceLeaderboard | null> {
    const res = await apiClient.get<CanopusPerformanceLeaderboard>(
      `/canopus-command/facilities/${facilityConfigId}/performance-leaderboard?period=${period}`
    );
    if (!res.success || !res.data) return null;
    return res.data;
  },

  async getTrends(
    facilityConfigId: string,
    period: CanopusSIPeriod
  ): Promise<CanopusTrendsResult | null> {
    const res = await apiClient.get<CanopusTrendsResult>(
      `/canopus-command/facilities/${facilityConfigId}/trends?period=${period}`
    );
    if (!res.success || !res.data) return null;
    return res.data;
  },
};
